import {
  SeuComponentSection,
  SeuComponentSectionContent,
  SeuComponentsTableRowDefinition,
  SeuComponentsTableRowValue,
  SeuStreamTableRowDefinition,
} from '@seu-definitions';
import { getUniqueId } from '@seu-helpers';
import {
  SEU_CALCULATION_SUCCESS_CODE,
  SeuApiConverter,
  SeuCalculationComponent,
  SeuCalculationComponentApi,
  SeuCalculationResponseApi,
  SeuCalculationResponseItem,
  SeuCalculationStream,
  SeuComponentSolutionItem,
  SeuComputationCombination,
  SeuComputationCombinationApi,
  SeuComputationState,
  SeuFromApiConverter,
  SeuIcon,
} from '@seu-types';
import { isEmpty } from 'lodash-es';

function hasCode(
  codeLetter: 'E' | 'C' | 'W' | 'I',
  item: SeuCalculationResponseApi
): boolean {
  return (
    item.globalCodes.some((code) =>
      code.toUpperCase().startsWith(codeLetter)
    ) ||
    item.components.some((component) =>
      component.codes?.some((code) => code.toUpperCase().startsWith(codeLetter))
    )
  );
}

export const combinationConverter: SeuApiConverter<
  SeuComputationCombination,
  SeuComputationCombinationApi
> = {
  fromApi: (data) => {
    return {
      interconnectionId: data.interconnectionId
        ? String(data.interconnectionId)
        : null,
      globalParameterSetId: data.globalParameterSetId
        ? String(data.globalParameterSetId)
        : null,
      operatingConditionIds: data.operatingConditionIds
        ? data.operatingConditionIds.map((operatingCondition) =>
            String(operatingCondition)
          )
        : [],
    };
  },
  toApi: () => {
    return {} as SeuComputationCombinationApi;
  },
};

export const calculationConverter: SeuApiConverter<
  SeuCalculationResponseItem,
  SeuCalculationResponseApi
> = {
  fromApi: (calculationResponseItem) => {
    let computationState: SeuComputationState = 'success';
    if (hasCode('E', calculationResponseItem)) {
      computationState = 'error';
    } else if (hasCode('C', calculationResponseItem)) {
      computationState = 'critical';
    } else if (hasCode('W', calculationResponseItem)) {
      computationState = 'warning';
    }

    return {
      uid: getUniqueId(),
      globalParameterSetId:
        calculationResponseItem.globalParameterSetId.toString(),
      interconnectionId: calculationResponseItem.interconnectionId.toString(),
      computationState,
      components: calculationResponseItem.components.map((item) =>
        calculationComponentConverter.fromApi(item)
      ),
      streams: calculationResponseItem.streams,
      globalCodes: calculationResponseItem.globalCodes.filter(
        (code) => code.toUpperCase() !== SEU_CALCULATION_SUCCESS_CODE
      ),
      opex: calculationResponseItem.opex,
      emissionLimits: calculationResponseItem.emissionLimits,
      operatingConditionId:
        calculationResponseItem.operatingConditionId.toString(),
      pollingIntervalSeconds: calculationResponseItem.pollingIntervalSeconds,
      calculationId: calculationResponseItem.calculationId,
    };
  },
  toApi: () => {
    return {} as SeuCalculationResponseApi;
  },
};

const calculationComponentConverter: SeuApiConverter<
  SeuCalculationComponent,
  SeuCalculationComponentApi
> = {
  fromApi: (input) => {
    return {
      ...input,
      componentId: String(input.componentId),
      solutions: input.solutionsPerSection,
    };
  },
  toApi: () => {
    return {} as SeuCalculationComponentApi;
  },
};

export const calculationToStreamTableConverter: SeuFromApiConverter<
  SeuCalculationResponseItem,
  SeuStreamTableRowDefinition[]
> = {
  fromApi: (input) => {
    const tableRows: SeuStreamTableRowDefinition[] = [];

    const tableRowMap = new Map<string, SeuStreamTableRowDefinition>();

    const streams: SeuCalculationStream[] = input.streams;

    streams.forEach((stream: SeuCalculationStream, streamIndex) => {
      const canvasStreamId = stream.canvasStreamId;
      const calcGroups = [
        stream.generalData,
        stream.gasMainComp,
        stream.gasSideComp,
        stream.otherData,
      ];
      const groupColors = [
        'text-blue-800',
        'text-blue-500',
        'text-blue-300',
        'text-blue-200',
      ];

      calcGroups.forEach((group, groupIndex) => {
        group.forEach((groupItem) => {
          const resultKey = groupItem.name;

          if (streamIndex === 0) {
            const units = groupItem.unitValues ?? [];
            if (units.length) {
              tableRowMap.set(resultKey, {
                key: resultKey,
                label: resultKey,
                units: units.map((item) => item.unit),
                defaultUnit: groupItem.defaultUnit,
                values: {},
                headerInfo: {},
                uid: getUniqueId(),
                color: groupColors[groupIndex] ?? 'text-gray-50',
              });
            }
          }

          const tableRowItem = tableRowMap.get(resultKey);
          if (tableRowItem) {
            const units = groupItem.unitValues ?? [];
            let icon: SeuIcon | undefined = undefined;
            const aggregateState = groupItem.aggregateState;

            switch (aggregateState) {
              case 'AGGREGATE_STATE_GAS':
                icon = 'gas-24';
                break;
              case 'AGGREGATE_STATE_LIQUID':
                icon = 'liquid-24';
                break;
              case 'AGGREGATE_STATE_SOLID':
                icon = 'solid-24';
                break;
              case 'AGGREGATE_STATE_GAS_AND_LIQUID':
                icon = 'liquid-gas-24';
                break;
            }

            tableRowItem.values[canvasStreamId] = units.map((item) => {
              let val: number | string = '';
              if (
                typeof item.value === 'number' ||
                typeof item.value === 'string'
              ) {
                val = item.value;
              }
              return { value: val, icon };
            });

            tableRowItem.headerInfo[canvasStreamId] = {
              canvasStreamId,
            };
          }

          if (streamIndex === streams.length - 1) {
            const tableRowItem = tableRowMap.get(resultKey);
            if (tableRowItem) {
              tableRows.push(tableRowItem);
            }
          }
        });
      });
    });

    return tableRows;
  },
};

export const calculationToComponentTableConverter: SeuFromApiConverter<
  SeuCalculationResponseItem,
  SeuComponentsTableRowDefinition[]
> = {
  fromApi: (input) => {
    const tableRows: SeuComponentsTableRowDefinition[] = [];

    const tableRowMap = new Map<string, SeuComponentsTableRowDefinition>();

    const components: SeuCalculationComponent[] = input.components;

    components.forEach((component: SeuCalculationComponent, componentIndex) => {
      if (componentIndex === 0) {
        tableRowMap.set('solutions', {
          uid: getUniqueId(),
          values: [],
          headerInfo: [],
        });
      }

      const rowValue: SeuComponentsTableRowValue = {
        key: component.componentId,
        errorCodes: component.codes ?? [],
        sections: [],
      };

      const headerInfo = {
        key: component.componentId,
        componentId: component.componentId,
        componentType: component.componentType,
      };

      component.solutions.forEach((solution) => {
        const componentSolutions: SeuComponentSolutionItem[] =
          solution.solutionsPerSection;
        const section: SeuComponentSection = {
          title: solution.name,
          content: [],
          copyToInput: solution.attributes?.['copySolutionToInput'] === true,
        };

        componentSolutions.forEach((solutionItem) => {
          if (!isEmpty(solutionItem.solutionsPerSection)) {
            const contentSectionsMap: Map<
              string,
              SeuComponentSectionContent[]
            > = new Map();

            solutionItem.solutionsPerSection.forEach((solution) => {
              const solutionKey = solution.name;
              contentSectionsMap.set(solutionKey, []);
              const componentSolutions: SeuComponentSolutionItem[] =
                solution.solutionsPerSection;
              componentSolutions.forEach((solutionItem) => {
                if (solutionItem.unitValues.length) {
                  if (contentSectionsMap.has(solutionKey)) {
                    contentSectionsMap.get(solutionKey)?.push({
                      name: solutionItem.name,
                      uid: getUniqueId(),
                      type: 'child',
                      unitValues: solutionItem.unitValues,
                      defaultUnit: solutionItem.defaultUnit,
                    });
                  }
                }
              });
            });
            section.content.push({
              name: solutionItem.name,
              uid: getUniqueId(),
              type: 'parent',
              contentSectionsMap,
            });
          } else {
            if (solutionItem.unitValues.length) {
              section.content.push({
                name: solutionItem.name,
                uid: getUniqueId(),
                type: 'child',
                unitValues: solutionItem.unitValues.map((item) => ({
                  ...item,
                  unit: item.unit === '' ? '-' : item.unit,
                })),
                defaultUnit: solutionItem.defaultUnit,
              });
            }
          }
        });

        rowValue.sections.push(section);
      });

      const tableRowItem = tableRowMap.get('solutions');
      if (tableRowItem) {
        tableRowItem.values.push(rowValue);
        tableRowItem.headerInfo.push(headerInfo);
      }

      if (componentIndex === components.length - 1) {
        const tableRowItem = tableRowMap.get('solutions');
        if (tableRowItem) {
          tableRows.push(tableRowItem);
        }
      }
    });

    return tableRows;
  },
};
